import React, { useEffect, useRef } from 'react';

export interface ModalDialogProps {
  isOpen: boolean;
  onClose?: () => void;
  title?: string;
  description?: string;
  children: React.ReactNode;
  maxWidthClass?: string;
  preventDismiss?: boolean;
}

export function ModalDialog({
  isOpen,
  onClose,
  title,
  description,
  children,
  maxWidthClass = 'max-w-md',
  preventDismiss = false,
}: ModalDialogProps) {
  const dialogRef = useRef<HTMLDialogElement>(null);
  const isMouseDownOnBackdrop = useRef(false);

  useEffect(() => {
    const dialog = dialogRef.current;
    if (!dialog) return;

    if (isOpen) {
      if (!dialog.open) {
        dialog.showModal();
      }
    } else {
      if (dialog.open) {
        dialog.close();
      }
    }
  }, [isOpen]);

  // Handle native ESC key (cancel event)
  const handleCancel = (e: React.SyntheticEvent<HTMLDialogElement, Event>) => {
    if (preventDismiss) {
      e.preventDefault();
      return;
    }
    if (onClose) {
      onClose();
    }
  };

  const handleMouseDown = (e: React.MouseEvent<HTMLDialogElement>) => {
    if (preventDismiss || !onClose) return;

    if (e.target !== dialogRef.current) {
      isMouseDownOnBackdrop.current = false;
      return;
    }

    const rect = dialogRef.current.getBoundingClientRect();
    const isOutside =
      e.clientX < rect.left ||
      e.clientX > rect.right ||
      e.clientY < rect.top ||
      e.clientY > rect.bottom;

    isMouseDownOnBackdrop.current = isOutside;
  };

  const handleTouchStart = (e: React.TouchEvent<HTMLDialogElement>) => {
    if (preventDismiss || !onClose) return;

    if (e.target !== dialogRef.current) {
      isMouseDownOnBackdrop.current = false;
      return;
    }

    const touch = e.touches[0];
    if (!touch) return;

    const rect = dialogRef.current.getBoundingClientRect();
    const isOutside =
      touch.clientX < rect.left ||
      touch.clientX > rect.right ||
      touch.clientY < rect.top ||
      touch.clientY > rect.bottom;

    isMouseDownOnBackdrop.current = isOutside;
  };

  // Handle click outside (on ::backdrop)
  const handleClick = (e: React.MouseEvent<HTMLDialogElement>) => {
    if (preventDismiss || !onClose) return;

    // Must have originated as a mousedown/touchstart on the backdrop
    if (!isMouseDownOnBackdrop.current) {
      return;
    }
    isMouseDownOnBackdrop.current = false;

    // Must be directly on dialog backdrop
    if (e.target !== dialogRef.current) {
      return;
    }

    // Ignore synthetic 0,0 clicks
    if (e.clientX === 0 && e.clientY === 0) {
      return;
    }

    const rect = dialogRef.current.getBoundingClientRect();
    const isOutside =
      e.clientX < rect.left ||
      e.clientX > rect.right ||
      e.clientY < rect.top ||
      e.clientY > rect.bottom;

    if (isOutside) {
      onClose();
    }
  };

  if (!isOpen) return null;

  return (
    <dialog
      ref={dialogRef}
      onCancel={handleCancel}
      onMouseDown={handleMouseDown}
      onTouchStart={handleTouchStart}
      onClick={handleClick}
      className={`fixed inset-0 m-auto p-0 rounded-[8px] bg-background-primary text-foreground-primary border border-border-tertiary shadow-drop-md outline-none backdrop:bg-base-black/60 backdrop:backdrop-blur-sm animate-in fade-in zoom-in-95 duration-150 z-50 w-full ${maxWidthClass}`}
    >
      <div
        className="p-5 space-y-4 max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
        onMouseDown={(e) => e.stopPropagation()}
        onTouchStart={(e) => e.stopPropagation()}
      >
        {(title || onClose) && (
          <div className="flex items-start justify-between gap-3 border-b border-border-line pb-3">
            <div>
              {title && <h2 className="text-sm font-semibold text-foreground-primary tracking-tight">{title}</h2>}
              {description && <p className="text-xs text-foreground-tertiary mt-0.5">{description}</p>}
            </div>
            {!preventDismiss && onClose && (
              <button
                type="button"
                onClick={onClose}
                className="shrink-0 p-1 text-foreground-tertiary hover:text-foreground-primary rounded transition-colors -mr-1 -mt-1"
                aria-label="Close dialog"
              >
                ✕
              </button>
            )}
          </div>
        )}
        {children}
      </div>
    </dialog>
  );
}
